import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getVoicePreferences,
  subscribeVoicePreferences,
  updateVoicePreferences,
  type ScreenFps,
  type ScreenResolution,
  type ScreenSurface,
} from '@/lib/voicePreferences';
import { Button, Alert } from '@heroui/react';
import { Segmented } from '@/components/Segmented';
import { SelectMenu } from '@/components/SelectMenu';
import { MicIcon, MicOffIcon } from '@/components/icons';
import { Switch } from '@/components/ui/Switch';
import { RangeSlider } from '@/components/ui/RangeSlider';
import { formatKeyCode } from '@/lib/keybindLabel';
import {
  FilterToggle,
  LevelMeter,
  SectionHeader,
} from '@/components/voicePreferences/VoicePrefControls';
import { useMicTest } from '@/components/voicePreferences/useMicTest';

/**
 * "Голос" tab in UserSettingsModal. Three blocks:
 *
 *   1. Mic test  — pick device, hear yourself, see a live level meter
 *      so the user can verify the mic actually picks up sound before
 *      joining a voice channel
 *   2. Filters   — noise suppression / echo cancel / AGC toggles
 *      (channel mode is the upper bound; user can only further DISABLE)
 *   3. Output    — speaker device picker (Chromium-only via setSinkId)
 *      and output gain slider
 *
 * Mic test uses getUserMedia + Web Audio API:
 *   stream → MediaStreamAudioSourceNode → AnalyserNode → level meter
 *   stream → GainNode → AudioDestinationNode (optional self-listen)
 *
 * Tearing down the test stream on unmount is critical — leaving an open
 * audio track means the browser's mic indicator stays red and battery
 * keeps draining. The cleanup runs in two paths (component unmount and
 * test-stopped-by-user) via the same stopTest() helper.
 */
export function VoicePreferencesTab() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(getVoicePreferences());
  const { inputDevices, outputDevices, testing, listenSelf, setListenSelf, level, permError, acquiring, startTest, stopTest } =
    useMicTest(prefs);
  const [capturingKey, setCapturingKey] = useState(false);

  // Capture the next keypress OR mouse button as the push-to-talk binding
  // (Esc cancels). Left/right mouse buttons are ignored — left clicks the UI,
  // right would clash with context menus.
  useEffect(() => {
    if (!capturingKey) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'Escape') {
        setCapturingKey(false);
        return;
      }
      updateVoicePreferences({ pttKey: e.code });
      setCapturingKey(false);
    };
    const onMouse = (e: MouseEvent) => {
      if (e.button === 0 || e.button === 2) return; // left / right reserved
      e.preventDefault();
      e.stopPropagation();
      updateVoicePreferences({ pttKey: `Mouse${e.button}` });
      setCapturingKey(false);
    };
    const stopAux = (e: MouseEvent) => e.preventDefault(); // side-button nav
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onMouse, true);
    window.addEventListener('auxclick', stopAux, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onMouse, true);
      window.removeEventListener('auxclick', stopAux, true);
    };
  }, [capturingKey]);

  // Live-update local mirror of prefs when something elsewhere changes
  // them (unlikely here but cheap to wire).
  useEffect(() => {
    return subscribeVoicePreferences(() => setPrefs(getVoicePreferences()));
  }, []);

  return (
    <div className="app-settings-groups flex flex-col gap-5">
      <section className="space-y-3">
        <SectionHeader
          number="01"
          title={t('prof.voice.micTitle')}
        />
        <SelectMenu
          value={prefs.inputDeviceId || 'default'}
          onChange={(v) => updateVoicePreferences({ inputDeviceId: v })}
          options={[
            { value: 'default', label: t('prof.voice.deviceDefault') },
            ...inputDevices
              .filter((d) => d.deviceId && d.deviceId !== 'default')
              .map((d) => ({ value: d.deviceId, label: d.label })),
          ]}
        />
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-foreground/[0.04] px-3 py-3">
          <Button
            size="sm"
            variant={testing ? 'danger' : 'primary'}
            className="shrink-0"
            isDisabled={acquiring}
            onPress={() => (testing ? stopTest() : void startTest())}
          >
            {testing ? <MicOffIcon className="size-4" /> : <MicIcon className="size-4" />}
            {testing ? t('prof.voice.stop') : t('prof.voice.testMic')}
          </Button>
          <div className="flex min-w-[8rem] flex-[1_1_8rem] flex-col gap-1.5">
            <LevelMeter level={level} active={testing} />
            <label className="flex items-center gap-2 text-xs text-ink-dim">
              <Switch
                checked={listenSelf}
                onChange={setListenSelf}
                disabled={!testing}
                label={t('prof.voice.listenSelf')}
              />
              <span>{t('prof.voice.listenSelf')}</span>
            </label>
          </div>
        </div>
        {permError && (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{permError}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}

        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="font-medium text-ink-muted">{t('prof.voice.inputVolume')}</span>
            <span className="text-xs text-ink-muted">{prefs.inputGain}%</span>
          </div>
          <RangeSlider
            ariaLabel={t('prof.voice.inputVolume')}
            min={0}
            max={200}
            step={5}
            value={prefs.inputGain}
            onChange={(v) => updateVoicePreferences({ inputGain: v })}
          />
          <p className="text-xs text-ink-muted">{t('prof.voice.inputVolumeHint')}</p>
        </div>

        <div className="mt-1 text-xs font-medium text-ink-muted">{t('prof.voice.transmitMode')}</div>
        <Segmented<'open' | 'ptt' | 'voice'>
          fullWidth
          className="app-voice-mode-grid"
          label={t('prof.voice.transmitMode')}
          value={prefs.micMode}
          onChange={(micMode) => updateVoicePreferences({ micMode })}
          options={[
            { value: 'open', label: t('prof.voice.modeOpen') },
            { value: 'ptt', label: t('prof.voice.modePtt') },
            { value: 'voice', label: t('prof.voice.modeVoice') },
          ]}
        />
        {prefs.micMode === 'ptt' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-foreground/[0.04] px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-sm font-light tracking-[-0.015em] text-ink">{t('prof.voice.pttKeyTitle')}</div>
              <div className="text-xs leading-snug text-ink-muted">
                {capturingKey
                  ? t('prof.voice.pttCapturing')
                  : t('prof.voice.pttHint')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCapturingKey(true)}
              className={
                'font-medium ml-auto min-h-9 shrink-0 rounded-full px-3 py-2 text-[13px] transition-colors ' +
                (capturingKey
                  ? 'bg-accent !text-bg'
                  : 'bg-accent/15 text-accent hover:bg-accent/25')
              }
            >
              {capturingKey ? t('prof.voice.pttWaiting') : formatKeyCode(prefs.pttKey)}
            </button>
          </div>
        )}
        {prefs.micMode === 'voice' && (
          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-ink-muted">{t('prof.voice.sensitivity')}</span>
              <span className="text-xs text-ink-muted">{prefs.vadSensitivity}%</span>
            </div>
            <RangeSlider
              ariaLabel={t('prof.voice.sensitivityAria')}
              min={0}
              max={100}
              step={5}
              value={prefs.vadSensitivity}
              onChange={(v) => updateVoicePreferences({ vadSensitivity: v })}
            />
            <p className="text-xs text-ink-muted">
              {t('prof.voice.sensitivityHint')}
            </p>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <SectionHeader
          number="02"
          title={t('prof.voice.processingTitle')}
          hint={t('prof.voice.processingHint')}
        />
        <FilterToggle
          label={t('prof.voice.neuralLabel')}
          desc={t('prof.voice.neuralDesc')}
          checked={prefs.neuralSuppression}
          onChange={(v) => updateVoicePreferences({ neuralSuppression: v })}
        />
        <FilterToggle
          label={t('prof.voice.bunkerFilterLabel')}
          desc={t('prof.voice.bunkerFilterDesc')}
          checked={prefs.bunkerFilter}
          onChange={(v) => updateVoicePreferences({ bunkerFilter: v })}
        />
        <FilterToggle
          label={t('prof.voice.deepFilterLabel')}
          badge="Beta"
          desc={t('prof.voice.deepFilterDesc')}
          checked={prefs.deepFilter}
          onChange={(v) => updateVoicePreferences({ deepFilter: v })}
        />
        {prefs.deepFilter && (
          <div className="space-y-1 pl-3">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-ink-muted">{t('prof.voice.intensity')}</span>
              <span className="text-xs text-ink-muted">{prefs.deepFilterAtten}%</span>
            </div>
            <RangeSlider
              ariaLabel={t('prof.voice.intensityAria')}
              min={0}
              max={100}
              step={5}
              value={prefs.deepFilterAtten}
              onChange={(v) => updateVoicePreferences({ deepFilterAtten: v })}
            />
            <p className="text-xs text-ink-muted">
              {t('prof.voice.intensityHint')}
            </p>
          </div>
        )}
        <FilterToggle
          label={t('prof.voice.echoLabel')}
          desc={t('prof.voice.echoDesc')}
          checked={prefs.echoCancellation}
          onChange={(v) => updateVoicePreferences({ echoCancellation: v })}
        />
      </section>

      <section className="space-y-3">
        <SectionHeader
          number="03"
          title={t('prof.voice.outputTitle')}
          hint={t('prof.voice.outputHint')}
        />
        <SelectMenu
          value={prefs.outputDeviceId || 'default'}
          onChange={(v) => updateVoicePreferences({ outputDeviceId: v })}
          options={[
            { value: 'default', label: t('prof.voice.deviceDefault') },
            ...outputDevices
              .filter((d) => d.deviceId && d.deviceId !== 'default')
              .map((d) => ({ value: d.deviceId, label: d.label })),
          ]}
        />
        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="font-medium text-ink-muted">
              {t('prof.voice.outputVolume')}
            </span>
            <span className="text-xs text-ink-muted">{prefs.outputGain}%</span>
          </div>
          <RangeSlider
            ariaLabel={t('prof.voice.outputVolume')}
            min={0}
            max={200}
            step={5}
            value={prefs.outputGain}
            onChange={(v) => updateVoicePreferences({ outputGain: v })}
          />
          <p className="text-xs text-ink-muted">
            {t('prof.voice.outputVolumeHint')}
          </p>
        </div>
      </section>

      <section className="space-y-3">
        <SectionHeader
          number="04"
          title={t('prof.voice.screenTitle')}
          hint={t('prof.voice.screenHint')}
        />

        {/* FPS selector */}
        <div>
          <div className="font-medium mb-1.5 text-[10px] text-ink-muted">{t('prof.voice.fps')}</div>
          <Segmented<ScreenFps>
            fullWidth
            label={t('prof.voice.fps')}
            value={prefs.screenFps}
            onChange={(screenFps) => updateVoicePreferences({ screenFps })}
            options={([15, 30, 60] as const).map((fps) => ({ value: fps, label: `${fps} fps` }))}
          />
          <p className="mt-1.5 text-xs text-ink-muted">
            {t('prof.voice.fpsHint')}
          </p>
        </div>

        {/* Resolution selector */}
        <div>
          <div className="font-medium mb-1.5 text-[10px] text-ink-muted">{t('prof.voice.resolution')}</div>
          <Segmented<ScreenResolution>
            fullWidth
            className="app-voice-choice-grid app-voice-resolution-grid"
            label={t('prof.voice.resolution')}
            value={prefs.screenResolution}
            onChange={(screenResolution) => updateVoicePreferences({ screenResolution })}
            options={(['720p', '1080p', '1440p', '4k', 'source'] as const).map((r) => ({
              value: r,
              label: r === 'source' ? t('prof.voice.resolutionNative') : r,
            }))}
          />
          <p className="mt-1.5 text-xs text-ink-muted">
            {t('prof.voice.resolutionHint')}
          </p>
        </div>

        {/* Surface picker hint */}
        <div>
          <div className="font-medium mb-1.5 text-[10px] text-ink-muted">{t('prof.voice.surfaceType')}</div>
          <Segmented<ScreenSurface>
            fullWidth
            className="app-voice-choice-grid"
            label={t('prof.voice.surfaceType')}
            value={prefs.screenSurface}
            onChange={(screenSurface) => updateVoicePreferences({ screenSurface })}
            options={(['any', 'monitor', 'window', 'browser'] as const).map((sfc) => ({
              value: sfc,
              label: t(`prof.voice.surface.${sfc}`),
            }))}
          />
        </div>

        {/* Audio Fallback toggle */}
        <FilterToggle
          label={t('prof.voice.fallbackLabel')}
          desc={t('prof.voice.fallbackHint')}
          checked={prefs.screenAudioFallback}
          onChange={(v) => updateVoicePreferences({ screenAudioFallback: v })}
        />

        {/* Adaptive quality toggle */}
        <FilterToggle
          label={t('prof.voice.autoQualityLabel')}
          desc={t('prof.voice.autoQualityHint')}
          checked={prefs.screenQualityAuto}
          onChange={(v) => updateVoicePreferences({ screenQualityAuto: v })}
        />
      </section>
    </div>
  );
}

/** Human label for a KeyboardEvent.code (push-to-talk key). */
